import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Upload, Download, Plus, Loader2, HelpCircle } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AccountTree } from '@/components/shared/AccountTree'
import { accountsService } from '@/services/accountsService'
import { Account } from '@/api/types'
import { useAuthStore } from '@/store/auth.store'

/**
 * Chart of Accounts - List Page
 * Tree structure view with import/export functionality
 */
export const ChartsOfAccountsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null)
  const [pageNumber] = useState(1)
  const [pageSize] = useState(1000) // Increased to ensure all master nodes are loaded
  const [isUserGuideOpen, setIsUserGuideOpen] = useState(false)

  useEffect(() => {
    if (branchId) {
      loadAccounts()
    }
  }, [branchId, pageNumber])

  const loadAccounts = async () => {
    if (!branchId) {
      console.warn('⚠️ Cannot load accounts: branchId is missing')
      return
    }

    try {
      console.log('🟢 Starting to load accounts, branchId:', branchId)
      setLoading(true)
      const response = await accountsService.getAllAccounts(branchId, pageNumber, pageSize)
      console.log('🟢 API Response received:', response)
      const accountsData = response.data || []
      console.log('🟢 Loaded accounts:', accountsData.length, accountsData)
      
      // Log all master nodes (parentId = 0 or null)
      const masterNodes = accountsData.filter((acc: Account) => {
        const parentId = acc.parentAccountId || acc.parentId
        return parentId === 0 || parentId === null || parentId === undefined
      })
      console.log('🟢 Master nodes found:', masterNodes.length, masterNodes.map(acc => ({
        name: acc.nameEn || acc.name,
        code: acc.code || acc.accountCode,
        id: acc.accountId || acc.id,
        parentId: acc.parentAccountId || acc.parentId
      })))
      
      if (accountsData.length === 0) {
        console.warn('⚠️ No accounts returned from API')
      }
      
      // Sort accounts by specific order: Liabilities, Assets, Equity, Revenue, Expenses
      // This ensures consistent ordering across all branches
      const accountOrder: { [key: string]: number } = {
        'liabilities': 1,
        'assets': 2,
        'equity': 3,
        'revenue': 4,
        'expenses': 5,
      }

      const getAccountOrder = (account: Account): number => {
        const name = (account.nameEn || account.name || account.nameAr || '').toLowerCase().trim()
        
        // Check for exact matches first
        for (const [key, order] of Object.entries(accountOrder)) {
          if (name === key) {
            return order
          }
        }
        
        // Check for partial matches (e.g., "Current Assets" contains "assets")
        for (const [key, order] of Object.entries(accountOrder)) {
          if (name.includes(key)) {
            return order
          }
        }
        
        // If no match, try to extract from code
        const code = account.code || account.accountCode || ''
        const numFromCode = code ? (parseInt(code) || parseInt(code.match(/\d+/)?.[0] || '0') || 0) : 0
        
        // Map code numbers to order (1=Liabilities, 2=Assets, 3=Equity, 4=Revenue, 7=Expenses)
        if (numFromCode === 1) return 1 // Liabilities
        if (numFromCode === 2) return 2 // Assets
        if (numFromCode === 3) return 3 // Equity
        if (numFromCode === 4) return 4 // Revenue
        if (numFromCode === 7) return 5 // Expenses
        
        // Default: put unmatched accounts at the end
        return 999
      }

      const sortedAccounts = accountsData.sort((a, b) => {
        const orderA = getAccountOrder(a)
        const orderB = getAccountOrder(b)
        
        // If same order, sort by code number
        if (orderA === orderB) {
          const codeA = a.code || a.accountCode || ''
          const codeB = b.code || b.accountCode || ''
          const numA = codeA ? (parseInt(codeA) || parseInt(codeA.match(/\d+/)?.[0] || '0') || 0) : 0
          const numB = codeB ? (parseInt(codeB) || parseInt(codeB.match(/\d+/)?.[0] || '0') || 0) : 0
          return numA - numB
        }
        
        return orderA - orderB
      })
      
      setAccounts(sortedAccounts)
    } catch (error: any) {
      const status = error.response?.status
      const msg = (error.response?.data?.message || error.message || '').toLowerCase()
      const isNoData = status === 404 || /not found|no data|no records/.test(msg)
      setAccounts([])
      if (!isNoData) {
        toast.error(error.response?.data?.message || error.message || t('chartsOfAccounts.failedToLoadAccounts'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account)
  }

  const handleAddChild = (parentAccount: Account) => {
    navigate('/charts-of-accounts/new', {
      state: { parentAccount },
    })
  }

  const handleAddNew = () => {
    if (selectedAccount) {
      // If an account is selected, pass it as parentAccount
      navigate('/charts-of-accounts/new', {
        state: { parentAccount: selectedAccount },
      })
    } else {
      // No account selected, navigate without parent
      navigate('/charts-of-accounts/new')
    }
  }

  const handleEdit = (account: Account) => {
    const accountId = account.accountId || account.id
    if (accountId) {
      navigate(`/charts-of-accounts/${accountId}`)
    }
  }

  const handleDelete = async (account: Account) => {
    if (!window.confirm(t('chartsOfAccounts.confirmDeleteAccount', { name: account.nameEn || account.name }))) {
      return
    }

    // TODO: Implement delete API if available
    toast.error(t('chartsOfAccounts.deleteFunctionalityNotYetImplemented'))
  }

  const handleImport = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !branchId) return

    try {
      setUploading(true)
      await accountsService.uploadExcel(branchId, file)
      toast.success(t('chartsOfAccounts.fileUploadedSuccess'))
      loadAccounts()
    } catch (error: any) {
      console.error('Failed to upload file:', error)
      toast.error(error.response?.data?.message || t('chartsOfAccounts.failedToUploadFile'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleExport = async () => {
    if (!branchId) return

    try {
      setLoading(true)
      const blob = await accountsService.downloadChartOfAccounts(branchId)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `chart-of-accounts-${branchId}-${new Date().toISOString()}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success(t('chartsOfAccounts.chartOfAccountsDownloadedSuccess'))
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('chartsOfAccounts.failedToDownloadChartOfAccounts'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600">{t('chartsOfAccounts.pleaseSelectBranchFirst')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('chartsOfAccounts.account')}
          description={t('chartsOfAccounts.diagramForChartOfAccounts')}
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsUserGuideOpen(true)}
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            {t('chartsOfAccounts.userGuide')}
          </Button>
          <Button
            variant="outline"
            onClick={handleImport}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('chartsOfAccounts.uploading')}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {t('chartsOfAccounts.importFile')}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={loading}
          >
            <Download className="w-4 h-4 mr-2" />
            {t('chartsOfAccounts.exportFile')}
          </Button>
          <Button 
            onClick={handleAddNew}
            disabled={!selectedAccount}
            title={!selectedAccount ? t('chartsOfAccounts.pleaseSelectAccountFirst') : ''}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('chartsOfAccounts.addAccount')}
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        className="hidden"
      />

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <ListLoader />
          ) : (
            <AccountTree
              accounts={accounts}
              branchId={branchId}
              onAccountSelect={handleAccountSelect}
              onAddChild={handleAddChild}
              onEdit={handleEdit}
              onDelete={handleDelete}
              selectedAccountId={selectedAccount?.accountId || selectedAccount?.id}
            />
          )}
        </CardContent>
      </Card>

      {/* User Guide Modal */}
      <Dialog open={isUserGuideOpen} onOpenChange={setIsUserGuideOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('chartsOfAccounts.excelFileUploadGuide')}</DialogTitle>
            <DialogDescription>
              {t('chartsOfAccounts.pleaseEnsureExcelFileFollowsColumnStructure')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold mb-3 text-lg">{t('chartsOfAccounts.requiredColumnStructure')}:</h3>
              
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-muted-foreground">Column</div>
                  <div className="col-span-4 font-semibold">Column Name</div>
                  <div className="col-span-7">Description</div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">A</div>
                  <div className="col-span-4 font-medium">AccountCode</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    Unique hierarchical code for the account (e.g., "1", "11", "1101", "1101010000000001")
                  </div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">B</div>
                  <div className="col-span-4 font-medium">AccountNameEn</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    Account name in English (e.g., "Liabilities", "Current Assets")
                  </div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">C</div>
                  <div className="col-span-4 font-medium">AccountNameAr</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    Account name in Arabic (can be same as English if not available)
                  </div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">D</div>
                  <div className="col-span-4 font-medium">DefaultType</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    Boolean value: "True" for detailed accounts, "False" for summary accounts, or "Credit"/"Debit" for account type
                  </div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">E</div>
                  <div className="col-span-4 font-medium">AccountLevel</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    Hierarchical level number: 1 (top-level) to 5 (most detailed)
                  </div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">F</div>
                  <div className="col-span-4 font-medium">IsTransactionable</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    "Yes" for accounts that can have transactions, "No" for summary accounts (typically Level 5 = "Yes", Level 1-4 = "No")
                  </div>
                </div>
                
                <div className="grid grid-cols-12 gap-2 items-center p-2 bg-background rounded border">
                  <div className="col-span-1 font-mono text-sm font-semibold text-blue-600">G</div>
                  <div className="col-span-4 font-medium">BranchId</div>
                  <div className="col-span-7 text-sm text-muted-foreground">
                    Branch ID number (usually "1" for main branch or specific branch number)
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-semibold mb-2 text-blue-900 dark:text-blue-100">Important Notes:</h4>
              <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200 list-disc list-inside">
                <li>The first row should contain the column headers exactly as shown above (AccountCode, AccountNameEn, AccountNameAr, DefaultType, AccountLevel, IsTransactionable, BranchId)</li>
                <li>Column names are case-sensitive and must match exactly</li>
                <li>AccountCode must be unique and follow hierarchical structure (parent codes are prefixes of child codes)</li>
                <li>AccountLevel: 1 = top-level categories, 5 = most detailed transactionable accounts</li>
                <li>IsTransactionable: Only Level 5 accounts should be "Yes", higher levels should be "No"</li>
                <li>DefaultType: Use "True" for detailed accounts, "False" for summary accounts</li>
                <li>BranchId should match your current branch ID</li>
                <li>Only .xlsx and .xls file formats are supported</li>
              </ul>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
              <h4 className="font-semibold mb-2 text-amber-900 dark:text-amber-100">Example:</h4>
              <div className="text-sm text-amber-800 dark:text-amber-200 font-mono bg-white dark:bg-gray-800 p-3 rounded border overflow-x-auto">
                <div className="grid grid-cols-7 gap-2 text-xs">
                  <div className="font-semibold">AccountCode</div>
                  <div className="font-semibold">AccountNameEn</div>
                  <div className="font-semibold">AccountNameAr</div>
                  <div className="font-semibold">DefaultType</div>
                  <div className="font-semibold">AccountLevel</div>
                  <div className="font-semibold">IsTransactionable</div>
                  <div className="font-semibold">BranchId</div>
                  <div>1</div>
                  <div>Liabilities</div>
                  <div>Liabilities</div>
                  <div>False</div>
                  <div>1</div>
                  <div>No</div>
                  <div>1</div>
                  <div>11</div>
                  <div>Non-current Liabilities</div>
                  <div>Non-current Liabilities</div>
                  <div>False</div>
                  <div>2</div>
                  <div>No</div>
                  <div>1</div>
                  <div>1101</div>
                  <div>Long term borrowing</div>
                  <div>Long term borrowing</div>
                  <div>False</div>
                  <div>3</div>
                  <div>No</div>
                  <div>1</div>
                  <div>1101010000000001</div>
                  <div>Shareholder loan</div>
                  <div>Shareholder loan</div>
                  <div>True</div>
                  <div>5</div>
                  <div>Yes</div>
                  <div>1</div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

